import { useAuth } from "@/src/contexts/auth.context";
import { useChatbot } from "@/src/hooks/features/chatbot/use-chatbot";
import { BlocksApiArticle, SelectedCategory } from "@/src/models/wiki.models";
import { useGlobalNewsQuery } from "@/src/services/api/news.api";
import {
  useWikiArticle,
  useWikiCategoriesQuery,
} from "@/src/services/wiki/wiki.hooks";
import { router } from "expo-router";
import { useState } from "react";
import { Platform, useWindowDimensions } from "react-native";

export enum WikiTab {
  ARTICLES = "LABEL_ARTICLES",
  SAVED = "LABEL_SAVED",
  DRAFT = "LABEL_DRAFT",
}

const BREAKPOINT_SM = 480;
const BREAKPOINT_LG = 1024;

export const isWeb = Platform.OS === "web";

export function useWikiArticles() {
  const { width } = useWindowDimensions();
  const { isAdmin } = useAuth();

  // The org config already carries widgetId and kbFolderId, so mounting the
  // widget needs no round-trip to the agent itself.
  const { chatbotConfig, isConfigLoading: isChatbotConfigLoading } =
    useChatbot();

  const [searchText, setSearchText] = useState("");
  const [active, setActive] = useState<WikiTab>(WikiTab.ARTICLES);
  const [selectedCategory, setSelectedCategory] =
    useState<SelectedCategory>(null);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(
    null,
  );

  const {
    data: newsData,
    isLoading: newsLoading,
    isError: newsError,
  } = useGlobalNewsQuery();

  const { data: selectedArticle, isLoading: articleLoading } = useWikiArticle(
    selectedArticleId ?? undefined,
    !!selectedArticleId,
  );

  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useWikiCategoriesQuery();

  const tabs: WikiTab[] = isAdmin
    ? [WikiTab.ARTICLES, WikiTab.SAVED, WikiTab.DRAFT]
    : [WikiTab.ARTICLES, WikiTab.SAVED];

  const isSmall = width < BREAKPOINT_SM;
  const isLarge = width >= BREAKPOINT_LG;
  const showSidePanel = isLarge;
  // Small screens show 2 cards per row (never 1); medium → 2; large → 3.
  const numColumns = isLarge ? 3 : 2;

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(searchText.toLowerCase()),
  );

  const itemWidthClass =
    numColumns === 3 ? "w-[32.4%]" : numColumns === 2 ? "w-[48%]" : "w-full";
  const columnWrapperStyle = numColumns > 1 ? { gap: 10 } : undefined;
  const contentContainerStyle = { gap: 12 };

  const showCategoryBack =
    active === WikiTab.ARTICLES && !!selectedCategory && !selectedArticleId;
  const showHeader = !selectedArticleId;

  const handleCategoryPress = (id: string) => {
    const category = categories.find((cat) => cat.categoryID === id);
    if (category) {
      setSelectedCategory({ id: category.categoryID, name: category.name });
      setSelectedArticleId(null);
    }
  };

  const handleArticlePress = (article: BlocksApiArticle) => {
    if (isWeb) {
      setSelectedArticleId(article.ItemId);
    } else {
      router.push({
        pathname: "/(tabs)/wiki/wiki-article-detail",
        params: { articleId: article.ItemId },
      });
    }
  };

  const handleBack = () => {
    if (selectedArticleId) {
      setSelectedArticleId(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
  };

  const handleTabChange = (tab: WikiTab) => {
    setActive(tab);
    setSelectedCategory(null);
    setSelectedArticleId(null);
  };

  return {
    isAdmin,
    searchText,
    setSearchText,
    tabs,
    active,
    handleTabChange,
    selectedCategory,
    filteredCategories,
    handleCategoryPress,
    categoriesLoading,
    categoriesError,
    selectedArticleId,
    selectedArticle,
    articleLoading,
    handleArticlePress,
    handleBack,
    isSmall,
    showSidePanel,
    numColumns,
    itemWidthClass,
    columnWrapperStyle,
    contentContainerStyle,
    showCategoryBack,
    showHeader,
    newsData,
    newsLoading,
    newsError,
    // chatbot — widgetId and agentName come directly from stored config
    chatbotWidgetId: chatbotConfig?.widgetId ?? null,
    isChatbotConfigLoading,
  };
}
